import { useMemo } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink/index";
import * as Cmp from "@impero-com/common/utils/comparison";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { TST_COLUMN_SIZE, TST_DEFAULT_PAGE_SIZE } from "@impero-com/ui";
import {
    FilterTarget,
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedUniqueValues,
    getPaginationRowModel,
    Column,
} from "@tanstack/react-table";

import { StepIndex, TActTableRowListDto } from "api/generated/model";
import { CustomEllipsisLabel } from "components/table/tsTable/CustomEllipsisLabel";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { TSTValidTagsSelectFilter } from "components/table/tsTable/TSTValidTagsSelectFilter";
import { localizeAssignedAs } from "org/user/utils/localization";
import { WebRoute } from "routes";
import { canViewTestingProgram } from "utils/actions";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import { buildTagColumn, getUniqueTagIds } from "../sharedTableUtils";

import {
    TESTING_ACTIVITY_LIST_COLUMN_IDS,
    TESTING_ACTIVITY_TABLE_STATE_STORAGE_KEY,
    testingActivityListColumnFiltersCodecMap,
    testingActivityListTableDefaultValues,
} from "./consts";

export interface TestingActivityRow {
    activityId: string;
    title: string;
    canView: boolean;
    testingProgramId: number;
    programName: string;
    programCanView: boolean;
    stepIndex: StepIndex;
    assignmentType: string;
    samplingTagIds: string[];
}

function buildRows(
    t: TFunction,
    data: TActTableRowListDto,
    userId: number
): TestingActivityRow[] {
    const resolveProgram = (testingProgramId: number) => {
        const programDto =
            data.linkedResources.testingProgramNamesDto[testingProgramId];
        return {
            programName:
                programDto?.resource ??
                t(
                    "AccessAndAssignment_TestingActivities_TestingProgram_Missing",
                    { id: testingProgramId }
                ),
            programCanView: programDto
                ? canViewTestingProgram(programDto.actions)
                : false,
        };
    };

    return data.content.flatMap(({ resource: activity, actions }) => {
        const steps = [
            { stepInfo: activity.responsibleInfo, stepIndex: "Step0" as const },
            ...(activity.firstReviewInfo
                ? [
                      {
                          stepInfo: activity.firstReviewInfo,
                          stepIndex: "Step1" as const,
                      },
                  ]
                : []),
            ...(activity.secondReviewInfo
                ? [
                      {
                          stepInfo: activity.secondReviewInfo,
                          stepIndex: "Step2" as const,
                      },
                  ]
                : []),
        ];

        const { programName, programCanView } = resolveProgram(
            activity.testingProgramId
        );
        // We only include steps that are incomplete and assigned to the current user,
        // because there could be multiple steps with various users for reviewers etc. for
        // a single activity but we only want to show the relevant ones for the current user.
        return steps
            .filter(
                ({ stepInfo }) =>
                    stepInfo.completionDate === null && stepInfo.user === userId
            )
            .map(({ stepIndex }) => ({
                activityId: activity.activityId,
                title: activity.title,
                canView: actions.view,
                testingProgramId: activity.testingProgramId,
                programName,
                programCanView,
                stepIndex,
                assignmentType: localizeAssignedAs(t, stepIndex),
                samplingTagIds: activity.samplingTags,
            }));
    });
}

export function useUserTestingActivityListTable(
    data: TActTableRowListDto,
    userId: number
) {
    const { t } = useTranslation();

    const tableData = useMemo(
        () => buildRows(t, data, userId),
        [t, data, userId]
    );
    const notificationLabel = t("Table_RemovedFiltersWarning");

    const stateAndOnChanges = usePresetTableSearchParams(
        TESTING_ACTIVITY_TABLE_STATE_STORAGE_KEY,
        testingActivityListTableDefaultValues,
        testingActivityListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );
    const columns = buildColumns(t, data);
    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns,
        getRowId: (row) => `${row.activityId}-${row.stepIndex}`,
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<TestingActivityRow>(),
        getSortedRowModel: getSortedRowModel<TestingActivityRow>(),
        getFilteredRowModel: getFilteredRowModel<TestingActivityRow>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<TestingActivityRow>(),
        getFacetedUniqueValues: getFacetedUniqueValues<TestingActivityRow>(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const filterComponent = (
        column: Column<TestingActivityRow>,
        target: FilterTarget
    ) => getFilterComponent(t, column, target, data);

    return {
        table,
        getFilterComponent: filterComponent,
        canAddException: (column: Column<TestingActivityRow>) =>
            column.id === TESTING_ACTIVITY_LIST_COLUMN_IDS.SAMPLING_TAGS,
        tableId: TESTING_ACTIVITY_TABLE_STATE_STORAGE_KEY,
    };
}

const columnHelper = createColumnHelper<TestingActivityRow>();
function buildColumns(t: TFunction, data: TActTableRowListDto) {
    return [
        columnHelper.accessor((row) => row.title, {
            id: TESTING_ACTIVITY_LIST_COLUMN_IDS.TESTING_ACTIVITY,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <ProtectedLink
                        to={`${WebRoute.TESTING_ACTIVITIES}/${row.activityId}`}
                        hasAccess={row.canView}
                    >
                        {info.getValue()}
                    </ProtectedLink>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<TestingActivityRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        TESTING_ACTIVITY_LIST_COLUMN_IDS.TESTING_ACTIVITY,
                        value
                    )
            ),
            header: () =>
                t("AccessAndAssignment_TestingActivities_TestingActivity"),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.programName, {
            id: TESTING_ACTIVITY_LIST_COLUMN_IDS.TESTING_PROGRAM,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <ProtectedLink
                        to={`${WebRoute.TESTING_PROGRAMS}/${row.testingProgramId}`}
                        hasAccess={row.programCanView}
                    >
                        {info.getValue()}
                    </ProtectedLink>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<TestingActivityRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        TESTING_ACTIVITY_LIST_COLUMN_IDS.TESTING_PROGRAM,
                        value
                    )
            ),
            header: () =>
                t("AccessAndAssignment_TestingActivities_TestingProgram"),
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.accessor((row) => row.assignmentType, {
            id: TESTING_ACTIVITY_LIST_COLUMN_IDS.ASSIGNMENT_TYPE,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: Cmp.map((row) => row.original.stepIndex),
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<TestingActivityRow, string[]>(
                (row, value) => value.includes(row.original.stepIndex)
            ),
            header: () =>
                t("AccessAndAssignment_TestingActivities_AssignmentType"),
            size: TST_COLUMN_SIZE.L,
        }),
        buildTagColumn(columnHelper, {
            id: TESTING_ACTIVITY_LIST_COLUMN_IDS.SAMPLING_TAGS,
            header: t("AccessAndAssignment_TestingActivities_SamplingTags"),
            selector: (row) => row.samplingTagIds,
            tags: data.linkedResources.tags,
            tagCategories: data.linkedResources.tagCategories,
        }),
    ];
}

type TestingActivityListColumnFilters = ZodCodecMapToType<
    typeof testingActivityListColumnFiltersCodecMap
>;

type TestingActivityFilterableColumnId = keyof TestingActivityListColumnFilters;

function getFilterComponent(
    t: TFunction,
    column: Column<TestingActivityRow>,
    target: FilterTarget,
    data: TActTableRowListDto
) {
    const getFilterHelpers =
        createFilterHelpersGetter<TestingActivityListColumnFilters>(column);
    const columnId = column.id as TestingActivityFilterableColumnId;

    switch (columnId) {
        case TESTING_ACTIVITY_LIST_COLUMN_IDS.TESTING_ACTIVITY:
        case TESTING_ACTIVITY_LIST_COLUMN_IDS.TESTING_PROGRAM: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((option) => ({
                label: option,
                value: option,
                customLabel: <CustomEllipsisLabel text={option} />,
            }));
            const placeholderKey = {
                [TESTING_ACTIVITY_LIST_COLUMN_IDS.TESTING_ACTIVITY]:
                    "AccessAndAssignment_TestingActivities_TestingActivity",
                [TESTING_ACTIVITY_LIST_COLUMN_IDS.TESTING_PROGRAM]:
                    "AccessAndAssignment_TestingActivities_TestingProgram",
            } as const;
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t(placeholderKey[columnId])}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case TESTING_ACTIVITY_LIST_COLUMN_IDS.ASSIGNMENT_TYPE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const stepIndexes = new Set(
                column
                    .getFacetedRowModel()
                    .flatRows.map((row) => row.original.stepIndex)
            );
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={[...stepIndexes].map((stepIndex) => ({
                        label: localizeAssignedAs(t, stepIndex),
                        value: stepIndex,
                    }))}
                    placeholder={t(
                        "AccessAndAssignment_TestingActivities_AssignmentType"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case TESTING_ACTIVITY_LIST_COLUMN_IDS.SAMPLING_TAGS: {
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );
            return (
                <TSTValidTagsSelectFilter
                    tagCategories={data.linkedResources.tagCategories}
                    tags={data.linkedResources.tags}
                    uniqueTagIds={getUniqueTagIds(
                        column,
                        (row) => row.samplingTagIds
                    )}
                    placeholder={t(
                        "AccessAndAssignment_TestingActivities_SamplingTags"
                    )}
                    setFilterValue={setFilterValue}
                    value={value}
                />
            );
        }
        default:
            return assertNever(columnId);
    }
}
